/**
 * Client-side session state.
 *
 * The browser never holds a token: the API sets an http-only cookie, and this
 * module simply asks `/api/auth/session` who is signed in. Every screen reads
 * the session through {@link useSession} so the answer is cached once and
 * revalidated on focus rather than re-fetched per component.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { ApiRequestError, apiBaseUrl, apiGet, apiPost } from './api-client';

export const APP_ROLES = ['owner', 'finance', 'approver', 'employee'] as const;
export type AppRole = (typeof APP_ROLES)[number];

export interface SessionUser {
  subject: string;
  email: string | null;
  displayName: string | null;
  groups: string[];
  roles: AppRole[];
}

export interface SessionEnvelope {
  user: SessionUser;
  session: {
    id: string;
    issuedAt: string;
    expiresAt: string;
    refreshExpiresAt: string;
  };
}

export type AuthProviderStatus = 'ok' | 'not_configured' | 'unreachable';

export interface AuthProviderConfig {
  provider: string;
  configured: boolean;
  status: AuthProviderStatus;
  issuerUrl: string | null;
  issuerEnvVar: string;
  missingEnvVars: string[];
  loginPath: string;
  detail: string | null;
}

export const sessionQueryKey = ['auth', 'session'] as const;
export const authConfigQueryKey = ['auth', 'config'] as const;

/**
 * The signed-in user, or `null` when nobody is. A 401 is an *answer*, not a
 * failure, so it resolves rather than throwing — screens branch on the value.
 */
export function useSession(): UseQueryResult<SessionEnvelope | null, ApiRequestError> {
  return useQuery<SessionEnvelope | null, ApiRequestError>({
    queryKey: sessionQueryKey,
    queryFn: async () => {
      try {
        return await apiGet<SessionEnvelope>('/api/auth/session');
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** What the sign-in screen needs: is Authentik configured, and is it answering? */
export function useAuthProviderConfig(): UseQueryResult<AuthProviderConfig, ApiRequestError> {
  return useQuery<AuthProviderConfig, ApiRequestError>({
    queryKey: authConfigQueryKey,
    queryFn: () => apiGet<AuthProviderConfig>('/api/auth/config'),
    staleTime: 15_000,
    retry: false,
  });
}

/** Only same-origin absolute paths are ever handed back to the API. */
export function safeReturnTo(candidate: string | null | undefined): string {
  if (
    candidate === null ||
    candidate === undefined ||
    candidate.length === 0 ||
    !candidate.startsWith('/') ||
    candidate.startsWith('//') ||
    candidate.startsWith('/login')
  ) {
    return '/';
  }
  return candidate;
}

/**
 * Hand the browser to Authentik. A full navigation (not fetch) is required:
 * the Authorization Code flow is a redirect chain the SPA cannot follow.
 */
export function startAuthentikLogin(returnTo?: string): void {
  const target = safeReturnTo(returnTo);
  const query = new URLSearchParams({ returnTo: target }).toString();
  window.location.assign(`${apiBaseUrl}/api/auth/login?${query}`);
}

export function useLogout(): { signOut: () => void; isPending: boolean; error: unknown } {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => apiPost<undefined>('/api/auth/logout'),
    onSuccess: async () => {
      queryClient.setQueryData(sessionQueryKey, null);
      await queryClient.invalidateQueries();
    },
  });

  const signOut = useCallback(() => {
    mutation.mutate();
  }, [mutation]);

  return { signOut, isPending: mutation.isPending, error: mutation.error };
}

/**
 * Ask the API to re-read the user's Authentik groups. Used after an owner edits
 * the role mapping so the change is visible without signing out.
 */
export function useRefreshSession(): { refresh: () => void; isPending: boolean } {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => apiPost<SessionEnvelope>('/api/auth/refresh'),
    onSuccess: (envelope) => {
      queryClient.setQueryData(sessionQueryKey, envelope);
    },
  });

  return { refresh: () => mutation.mutate(), isPending: mutation.isPending };
}

export interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: SessionUser | null;
  error: ApiRequestError | null;
  hasRole: (...roles: readonly AppRole[]) => boolean;
}

/** Convenience view over {@link useSession} for guards and nav filtering. */
export function useAuth(): AuthState {
  const query = useSession();
  const user = query.data?.user ?? null;

  const hasRole = useCallback(
    (...roles: readonly AppRole[]): boolean =>
      user !== null && roles.some((role) => user.roles.includes(role)),
    [user],
  );

  return useMemo(
    () => ({
      isLoading: query.isPending,
      isAuthenticated: user !== null,
      user,
      error: query.error ?? null,
      hasRole,
    }),
    [query.isPending, query.error, user, hasRole],
  );
}

export const ROLE_LABELS: Record<AppRole, string> = {
  owner: 'Owner',
  finance: 'Finance',
  approver: 'Approver',
  employee: 'Employee',
};
